import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Flame, ArrowRight } from 'lucide-react';
import { DjayAvatar } from '../../../mascot';
import { type CelebrationTriggers } from '../../hooks/useSessionExperience';
import { DjayGrowthState } from '../../../components/shared/DjayGrowthState';
import { FAMILY_DISPLAY_NAMES } from '../../../content/familyLabels';
import { hapticManager } from '../../../utils/hapticManager';
import { SummaryShell } from './SummaryShell';
interface CelebrationMomentsProps {
  triggers: CelebrationTriggers;
  onComplete: () => void;
}
type MomentType = 'foutloos' | 'record' | 'stageUp' | 'streak';
export function CelebrationMoments({
  triggers,
  onComplete
}: CelebrationMomentsProps) {
  const [moments, setMoments] = useState<MomentType[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    const queue: MomentType[] = [];
    if (triggers.foutloos) queue.push('foutloos');
    if (triggers.recordBroken) queue.push('record');
    if (triggers.stageUpgrades.length > 0) queue.push('stageUp');
    if (triggers.streakMilestone) queue.push('streak');
    setMoments(queue.slice(0, 3)); // hard cap
  }, [triggers]);
  useEffect(() => {
    if (moments.length === 0) {
      onComplete();
      return;
    }
    if (currentIndex >= moments.length) {
      onComplete();
      return;
    }
    const timer = setTimeout(() => {
      setCurrentIndex(prev => prev + 1);
    }, 1800);
    return () => clearTimeout(timer);
  }, [currentIndex, moments, onComplete]);
  const handleSkip = () => {
    hapticManager.hapticSelection();
    onComplete();
  };
  if (moments.length === 0 || currentIndex >= moments.length) return null;
  const currentMoment = moments[currentIndex];
  return <SummaryShell tone="celebrated" cornerSlot={<button type="button" onClick={handleSkip} aria-label="Door naar overzicht" className="flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-slate-600 shadow-md backdrop-blur transition-transform hover:scale-105 active:scale-95" data-id="element-11132">
          <ArrowRight className="h-5 w-5" data-id="element-11133" />
        </button>} data-id="element-11131">
      {/* Whole-area click to skip (excluding the corner slot) */}
      <button type="button" onClick={handleSkip} aria-label="Tik om verder te gaan" className="absolute inset-0 cursor-pointer" data-id="element-11134" />

      <div className="relative flex flex-col items-center text-center" data-id="element-11135">
        {/* Progress dots */}
        <div className="mb-8 flex gap-1.5" data-id="element-11136">
          {moments.map((_, idx) => <div key={idx} className={`h-1.5 rounded-full transition-all ${idx <= currentIndex ? 'w-8 bg-amber-400' : 'w-6 bg-slate-200'}`} data-id="element-11137" />)}
        </div>

        <AnimatePresence mode="wait" data-id="element-11138">
          <motion.div key={currentMoment} initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 1.02,
          y: -10
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center" data-id="element-11139">
            {currentMoment === 'foutloos' && <>
                <DjayAvatar emotion="cheering" size="lg" className="mb-6" data-id="element-11140" />
                <div className="mb-5 flex gap-1.5" data-id="element-11141">
                  {Array.from({
                length: 5
              }).map((_, i) => <motion.div key={i} initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: i * 0.08,
                type: 'spring'
              }} className="h-5 w-5 rounded-full bg-emerald-500 ring-4 ring-emerald-200" data-id="element-11142" />)}
                </div>
                <h2 className="text-3xl font-bold text-slate-800" data-id="element-11143">Foutloos!</h2>
                <p className="mt-1.5 text-base text-slate-600" data-id="element-11144">
                  Alles in één keer goed.
                </p>
              </>}

            {currentMoment === 'record' && <>
                <DjayAvatar emotion="celebrating" size="lg" className="mb-6" data-id="element-11145" />
                <motion.div initial={{
              scale: 0,
              rotate: -10
            }} animate={{
              scale: 1,
              rotate: 0
            }} transition={{
              type: 'spring',
              bounce: 0.5
            }} className="mb-5 rounded-full bg-amber-100 p-5" data-id="element-11146">
                  <Trophy className="h-14 w-14 text-amber-500" data-id="element-11147" />
                </motion.div>
                <h2 className="text-3xl font-bold text-slate-800" data-id="element-11148">
                  Nieuw record!
                </h2>
                <p className="mt-1.5 text-base text-slate-600" data-id="element-11149">
                  Sneller of beter dan ooit.
                </p>
              </>}

            {currentMoment === 'stageUp' && triggers.stageUpgrades[0] !== undefined && <>
                  <DjayAvatar emotion="proud" size="lg" className="mb-6" data-id="element-11150" />
                  <div className="mb-5 flex items-center gap-3" data-id="element-11151">
                    <DjayGrowthState level={triggers.stageUpgrades[0].oldStage.level} size="lg" className="opacity-50 grayscale" data-id="element-11152" />
                    <motion.div initial={{
                x: -10,
                opacity: 0
              }} animate={{
                x: 0,
                opacity: 1
              }} transition={{
                delay: 0.3
              }} data-id="element-11153">
                      <Star className="h-7 w-7 text-emerald-400" data-id="element-11154" />
                    </motion.div>
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 0.5,
                type: 'spring'
              }} data-id="element-11155">
                      <DjayGrowthState level={triggers.stageUpgrades[0].newStage.level} size="lg" data-id="element-11156" />
                    </motion.div>
                  </div>
                  <h2 className="text-3xl font-bold text-slate-800" data-id="element-11157">
                    Niveau omhoog!
                  </h2>
                  <p className="mt-1.5 text-base text-slate-600" data-id="element-11158">
                    {FAMILY_DISPLAY_NAMES[triggers.stageUpgrades[0].familyId] ?? 'Deze vragen'}{' '}
                    gaan steeds beter.
                  </p>
                </>}

            {currentMoment === 'streak' && <>
                <DjayAvatar emotion="celebrating" size="lg" className="mb-6" data-id="element-11159" />
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1.1
            }} transition={{
              type: 'spring',
              bounce: 0.6
            }} className="mb-5 rounded-full bg-orange-100 p-5" data-id="element-11160">
                  <Flame className="h-14 w-14 text-orange-500" data-id="element-11161" />
                </motion.div>
                <h2 className="text-3xl font-bold text-slate-800" data-id="element-11162">
                  {triggers.streakMilestone} dagen!
                </h2>
                <p className="mt-1.5 text-base text-slate-600" data-id="element-11163">
                  Wat een doorzettingsvermogen.
                </p>
              </>}
          </motion.div>
        </AnimatePresence>

        <p className="mt-10 text-xs text-slate-400" data-id="element-11164">Tik om verder te gaan</p>
      </div>
    </SummaryShell>;
}